import React, { useEffect, useRef, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { request } from "../../../../../utils/Request";
import { dateToUtcDate, getUserTimezone, setLastUpdate, utcDateToLocalDate } from "../../../../../utils/Helper";
import { Field, Form, Formik, FormikProps } from "formik";
import * as yup from "yup";
import { get, includes, isEmpty } from "lodash";
import moment from "moment";
import { Sidebar } from "primereact/sidebar";
import { InputField } from "../../../../../components/Shared/InputField";
import { Col, Row } from "react-bootstrap";
import { InputTextAreaField } from "../../../../../components/Shared/InputTextAreaField";
import { Button } from "primereact/button";
import InputDateTime from "../../../../../components/Shared/InputDateTime";
import { InputSingleSelectField } from "../../../../../components/Shared/InputSingleSelectField";
import Location from "../../../../../components/Common/Display/Location";
import AlpOutboundLoadDocument from "../AlpOutboundLoad/AlpOutboundLoadDocument";

interface IFormProps {
    id: string | null,
    t: any | null,
    onRefresh?: any | null,
    dropdowns?: any | null,
    onClose?: any | null,
    toastify?: any | null,
    providerUploadSlug?: string | null,
}

type TInputForm = {
    driver_id?: any,
    transport_vehicle_id?: any,
    estimated_loading_time?: any,
    estimated_unloading_time?: any,
    comments?: any,
    id?: any,
    cmr?: any,
};

const DEFAULT_FORM_STATE: TInputForm = {
    driver_id: null,
    transport_vehicle_id: null,
    estimated_loading_time: null,
    estimated_unloading_time: null,
    comments: null,
    id: null,
    cmr: null,
};

const validationSchema = (t: Function): any => {
    return yup.object().shape({
        driver_id: yup.string().required(t('the field is required.', { field: t('driver') })),
        transport_vehicle_id: yup.string().required(t('the field is required.', { field: t('transport vehicle') })),
        estimated_loading_time: yup.date(t("the field value is not valid", { field: t('estimated loading time') }))
            .required(t('the field is required.', { field: t('estimated loading time') })).nullable(),
        estimated_unloading_time: yup.date(t("the field value is not valid", { field: t('estimated unloading time') }))
            .min(yup.ref('estimated_loading_time'), t('the field must be a date after', {
                field: "estimated unloading time",
                fieldAfter: "estimated loading time"
            }))
            .required(t('the field is required.', { field: t('estimated loading time') })).nullable(),
        comments: yup.string().nullable(),
        cmr: yup.string().nullable(),
    });
};

function AlpReexportLoadAccept(props: IFormProps) {
    const { dropdowns, toastify } = props;
    const { t } = useTranslation();
    const formRef: any = useRef();
    const documentRef: any = useRef();
    const [entity, setEntity] = useState<any>(null);
    const [initFormState, setInitFormState] = useState<any>({ ...DEFAULT_FORM_STATE });
    const [panelState, setPanelState] = useState<boolean>(false);
    const providerUploadSlug = props.providerUploadSlug
        ?? entity?.provider_slug
        ?? entity?.providerSlug
        ?? entity?.provider?.slug
        ?? dropdowns?.providers?.find((provider: any) => provider.id === entity?.provider_id)?.slug
        ?? dropdowns?.providers?.find((provider: any) => provider.id === entity?.provider_id)?.id
        ?? entity?.source_type
        ?? 'emil_frey';

    const onClose = () => {
        setPanelState(false);
        documentRef.current?.resetDocuments();

        if ( props.onClose ) {
            props.onClose();
        }
    };

    const onSubmit = async (payloads: TInputForm, { setErrors, resetForm }: any) => {
        const documents = documentRef.current?.getDocuments() || [];

        try {
            await request({
                method: 'POST',
                url: `/suppliers/alp/reexport/loads/${ props.id }/accept`,
                data: {
                    ...payloads,
                    estimated_unloading_time: (( payloads.estimated_unloading_time ) ? dateToUtcDate(payloads.estimated_unloading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss') : null),
                    estimated_loading_time: (( payloads.estimated_loading_time ) ? dateToUtcDate(payloads.estimated_loading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss') : null),
                    documents: documents.map((item: any) => ({
                        id: item.document_id,
                        title: item.title
                    }))
                },
            });

            toastify(t("record updated", { item: t('self pickup load') }), "info");
            setLastUpdate(payloads.id, 'AlpReexportLoad');
            resetForm();
            onClose();

            if ( props.onRefresh ) {
                props.onRefresh();
            }
        } catch (e: any) {
            if ( e.status === 422 ) {
                setErrors(e.data.errors);
            } if ( e.status === 404 ) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    const getDetail = async (id: string) => {
        try {
            const response = await request({
                url: `/suppliers/alp/reexport/loads/${ id }`
            });
            const { data } = response.data;
            setEntity(data);
            setInitFormState({
                id: data.id,
                cmr: data.cmr ?? null,
                driver_id: get(data, ['driver', 'id']) ?? null,
                transport_vehicle_id: get(data, ['transport_vehicle', 'id']) ?? null,
                estimated_unloading_time: (( data.estimated_unloading_time ) ? utcDateToLocalDate(data.estimated_unloading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss') : null),
                estimated_loading_time: (( data.estimated_loading_time ) ? utcDateToLocalDate(data.estimated_loading_time, 'YYYY-MM-DD[T]HH:mm:ss', 'YYYY-MM-DD HH:mm:ss') : null),
            });
            documentRef.current?.resetDocuments();
            setPanelState(true);
        } catch (error: any) {
            if ( error.status === 404 ) {
                toastify(t("action not allowed: 404"), "error");
            } else {
                toastify(t("server error"), "error");
            }
        }
    };

    useEffect(() => {
        setEntity(null);
        const currentId = props.id;
        if ( typeof currentId === 'string' && !isEmpty(currentId) ) {
            getDetail(currentId);
        }
    }, [props.id]);

    const getDates = (_startDate: any, _stopDate: any) => {
        let dateArray = [];
        let currentDate = _startDate;
        let stopDate = _stopDate;
        let countHours = 0;

        while ( currentDate <= stopDate ) {
            dateArray.push(moment(currentDate).format('YYYY-MM-DD'));
            countHours = (includes(['Sunday', 'Saturday'], currentDate.format('dddd'))) ? countHours + 24 : countHours;
            currentDate = moment(currentDate).add(1, 'days');
        }

        if ( includes(['Sunday'], currentDate.format('dddd')) ) {
            countHours += 24;
        }

        return countHours;
    };

    return (
        <div>
            {
                entity &&
                <Sidebar
                    header={
                        <div className="bg-body-tertiary card-header">
                            <h5 className={ "card-title mt-0" }>{ t('accept load') }{ entity && ` #${ entity.load_number }` }</h5>
                            <p>{ t("using this form you can add or update the record") }</p>
                        </div>
                    }
                    visible={ panelState } position="right" onHide={ onClose } blockScroll={ true }
                    style={ { width: '900px' } } closeOnEscape={ false }
                >
                    <div style={ {
                        height: '3px',
                        background: 'linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)'
                    } }></div>

                    <Formik
                        innerRef={ formRef }
                        enableReinitialize={ true }
                        initialValues={ initFormState }
                        onSubmit={ onSubmit }
                        validationSchema={ validationSchema(t) }
                    >
                        { (props: FormikProps<TInputForm>) => {
                            return (
                                <Form onSubmit={ props.handleSubmit }>
                                    <Row className={ "mt-4 mb-7" }>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSingleSelectField }
                                                   name="driver_id"
                                                   options={ dropdowns.drivers.filter((item: any) => item.is_active != false) }
                                                   label={ t('driver') }
                                                   placeholder={ t('select') + ' ' + t('driver') }
                                                   isRequired />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputSingleSelectField }
                                                   panelClassName={ "hide-p-toggler" }
                                                   name="transport_vehicle_id"
                                                   options={ dropdowns
                                                       .transportVehicles
                                                       .filter((item: any) => item.is_active != false)
                                                       .map((item: any) => ({
                                                           ...item,
                                                           label: `${ item.truck_plate_number } | ${ item.trailer_plate_number }`,
                                                           icon: (!isEmpty(item.active_journey) ? 'fas fa-circle text-yellow-500' : 'fas fa-circle text-green-500')
                                                       }))
                                                   }
                                                   label={ t('transport vehicle') }
                                                   placeholder={ t('select') + ' ' + t('transport vehicle') }
                                                   isRequired />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field
                                                placeholder={ t("pick a date") }
                                                component={ InputDateTime }
                                                name={ `estimated_loading_time` }
                                                type={ "datetime" }
                                                label={ t("estimated loading time") }
                                                isRequired />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field
                                                placeholder={ t("pick a date") }
                                                component={ InputDateTime }
                                                name={ `estimated_unloading_time` }
                                                type={ "datetime" }
                                                label={ t("estimated unloading time") }
                                                minDate={ moment.utc(entity.created_at, 'YYYY-MM-DD HH:mm:ss').clone().tz(getUserTimezone()).toDate() }
                                                isRequired />
                                        </Col>
                                        <Col sm={ 12 } md={ 6 }>
                                            <Field component={ InputField }
                                                   name="cmr"
                                                   label={ t('cmr') }
                                                   placeholder={ t("enter") + ' ' + t("cmr").toLowerCase() } />
                                        </Col>
                                        <Col sm={ 12 } md={ 12 }>
                                            <Field component={ InputTextAreaField }
                                                   name="comments"
                                                   label={ t('comments') }
                                                   placeholder={ t("enter") + ' ' + t("comments").toLowerCase() } />
                                        </Col>

                                        <Col sm={ 12 } className={ "mt-3 mb-3" }>
                                            <AlpOutboundLoadDocument
                                                ref={ documentRef }
                                                dropdowns={ dropdowns }
                                                providerUploadSlug={ providerUploadSlug }
                                            />
                                        </Col>

                                        <Col sm={ 12 } md={ 12 } className={ "mb-5" }>
                                            <Row>
                                                <Col md="6" lg="6" sm="12">
                                                    <h6><span className="text-capitalize">{ t('supplier compound') }</span></h6>
                                                </Col>
                                                <Col md="6" lg="6" sm="12">
                                                    <div className="text-end text-uppercase">
                                                        <div>{ entity.supplier_compound }</div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <hr />
                                            <h4 className="card-title">{ t('orders') }</h4>
                                            <div className={ 'table-responsive' }>
                                                <table className={ "fl-table" }>
                                                    <thead>
                                                    <tr>
                                                        <th>#</th>
                                                        <th>{ t('from location') }</th>
                                                        <th>{ t('to location') }</th>
                                                        <th>{ t('brand') }</th>
                                                        <th>{ t('model') }</th>
                                                        <th>{ t('order code') }</th>
                                                        <th>{ t('vin number') }</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {
                                                        entity.alp_reexport_load_orders.map((row: any, index: number) => (
                                                            <tr key={ index }>
                                                                <th>{ index + 1 }</th>
                                                                <td><Location item={ get(entity, ['from_location']) } titled={ 1 } /></td>
                                                                <td><Location item={ get(entity, ['to_location']) } titled={ 1 } /></td>
                                                                <td className={ "text-capitalize" }>{ get(row, ['order', 'brand', 'title']) }</td>
                                                                <td className={ "text-capitalize" }>{ get(row, ['order', 'brand_model', 'title']) } - { get(row, ['order', 'brand_model', 'version_code']) }</td>
                                                                <td>{ get(row, ['order', 'order_cd']) }</td>
                                                                <td>{ get(row, ['order', 'vin_number']) }</td>
                                                            </tr>
                                                        ))
                                                    }
                                                    </tbody>
                                                </table>
                                            </div>
                                        </Col>
                                    </Row>
                                    <div className="filter-card-footer" style={ { width: '885px' } }>
                                        <Button tooltip={ t("to data save into database") }
                                                size={ "small" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                                disabled={ props.isSubmitting }>
                                            <i className="pi pi-save me-2" />
                                            { t("let's save") }
                                        </Button>
                                        <Button type={ "button" } tooltip={ t("operation cancel") } className={ "ms-2" }
                                                size={ "small" } severity={ "danger" } outlined
                                                tooltipOptions={ { position: 'top' } }
                                                onClick={ onClose } disabled={ props.isSubmitting }>
                                            { t("cancel") }
                                        </Button>
                                    </div>
                                </Form>
                            );
                        } }
                    </Formik>
                </Sidebar>
            }
        </div>
    );
}

AlpReexportLoadAccept.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpReexportLoadAccept);
